/**
 * TanStack Query hooks for `api-tracking`. Thin wrappers over ./api.
 * Two mutations (logClick, logConvert) + two on-demand lookups.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	postClick,
	postConvert,
	getClick,
	getConversion,
	apiTrackingQueryKeys,
	type ApiTrackingClickBody,
	type ApiTrackingClickResponse,
	type ApiTrackingConvertBody,
	type ApiTrackingConvertResponse,
	type ApiTrackingClickRow,
	type ApiTrackingConversionRow,
} from "./api";

export function useApiTrackingLogClick(): UseMutationResult<
	ApiTrackingClickResponse,
	Error,
	ApiTrackingClickBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: postClick,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: apiTrackingQueryKeys.all });
		},
	});
}

export function useApiTrackingLogConvert(): UseMutationResult<
	ApiTrackingConvertResponse,
	Error,
	ApiTrackingConvertBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: postConvert,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: apiTrackingQueryKeys.all });
		},
	});
}

export function useApiTrackingClick(
	clickId: string | null,
): UseQueryResult<{ click: ApiTrackingClickRow }, Error> {
	return useQuery({
		queryKey: apiTrackingQueryKeys.click(clickId ?? ""),
		queryFn: () => getClick(clickId as string),
		enabled: !!clickId,
	});
}

export function useApiTrackingConversion(
	conversionId: string | null,
): UseQueryResult<{ conversion: ApiTrackingConversionRow }, Error> {
	return useQuery({
		queryKey: apiTrackingQueryKeys.conversion(conversionId ?? ""),
		queryFn: () => getConversion(conversionId as string),
		enabled: !!conversionId,
	});
}
